import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, ShieldCheck, Sparkles, User } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const adminNotes = [
  {
    icon: ShieldCheck,
    title: 'Protected access',
    description: 'Use your admin credentials to access bookings, testimonials, and dashboard tools.',
  },
  {
    icon: Sparkles,
    title: 'Simple control panel',
    description: 'Manage core site activity from one place once you are signed in.',
  },
];

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate('/admin/dashboard');
    } catch (error) {
      localStorage.removeItem('admin_token');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!credentials.username || !credentials.password) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);

      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        toast.success('Login successful!');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen overflow-hidden bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 text-slate-900 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)] dark:text-white"
      data-testid="admin-login-page"
    >
      <div className="relative isolate flex min-h-[calc(100vh-4rem)] items-center">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-400/10" />
          <div className="absolute right-[-6rem] top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl dark:bg-blue-500/20" />
          <div className="absolute bottom-[-5rem] left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.8),transparent_45%)] dark:bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_35%)]" />
        </div>

        <div className="relative mx-auto grid w-full max-w-6xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
          <div className="flex items-center">
            <div className="max-w-2xl">
              <div className="mb-6 inline-flex items-center rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-medium text-sky-700 shadow-sm backdrop-blur dark:border-sky-400/20 dark:bg-white/5 dark:text-sky-200">
                Secure admin access
              </div>
              <h1 className="text-4xl font-black tracking-tight text-slate-950 sm:text-5xl lg:text-6xl dark:text-white">
                Access the dashboard with
                <span className="block bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  clarity and control.
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600 dark:text-slate-300">
                Sign in to manage bookings, review incoming activity, and keep the admin side of
                Connectify moving smoothly.
              </p>

              <div className="mt-10 grid gap-4">
                {adminNotes.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.4)] backdrop-blur dark:border-white/10 dark:bg-white/5"
                  >
                    <div className="flex items-start gap-4">
                      <div className="rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 p-3 text-white">
                        <item.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-slate-950 dark:text-white">{item.title}</h2>
                        <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-sky-400/20 via-cyan-300/10 to-emerald-300/20 blur-2xl dark:from-sky-400/15 dark:to-emerald-400/10" />
            <div className="relative rounded-[2rem] border border-white/70 bg-white/85 p-8 shadow-[0_30px_80px_-24px_rgba(2,12,27,0.25)] backdrop-blur dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.88),rgba(15,23,42,0.72))] dark:shadow-[0_30px_80px_-24px_rgba(2,12,27,0.7)]">
              <div className="mb-8 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-lg">
                  <Lock className="h-8 w-8" />
                </div>
                <h2 className="text-3xl font-bold text-slate-950 dark:text-white">Admin Login</h2>
                <p className="mt-2 text-slate-600 dark:text-slate-300">Access the admin dashboard</p>
              </div>

              <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Username
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <User className="h-5 w-5 text-slate-400" />
                    </div>
                    <Input
                      type="text"
                      data-testid="admin-username-input"
                      value={credentials.username}
                      onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                      placeholder="Enter username"
                      className="border-slate-200 bg-white pl-10 dark:border-white/10 dark:bg-white dark:text-slate-900"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Password
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <Input
                      type="password"
                      data-testid="admin-password-input"
                      value={credentials.password}
                      onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                      placeholder="Enter password"
                      className="border-slate-200 bg-white pl-10 dark:border-white/10 dark:bg-white dark:text-slate-900"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="admin-login-btn"
                  disabled={loading}
                  className="w-full bg-[#1E90FF] py-6 text-white transition duration-300 hover:bg-[#1570d6] hover:shadow-[0_20px_50px_-20px_rgba(30,144,255,0.55)]"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>

              <div className="mt-6 rounded-[1.5rem] border border-sky-200 bg-sky-50/80 p-5 text-center dark:border-white/10 dark:bg-white/5">
                <p className="text-xs leading-6 text-slate-600 dark:text-slate-300">
                  Default credentials:
                  <br />
                  <span className="font-mono font-semibold text-slate-800 dark:text-white">Username: </span>
                  <br />
                  <span className="font-mono font-semibold text-slate-800 dark:text-white">Password: </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
